import { useGetDiscountNudgeVAEQuery } from "@src/services/rollingStartApi";
import { useMemo } from "react";
import {
  EStoreExclusion,
  IDiscountNudgeVAE,
} from "@src/lib/DiscountProduct/DiscountProduct.interface";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";
import useCustomerModelChange from "@src/backbone/hooks/useCustomerModelChange";
import { ECustomerGroup } from "@src/interface/Customer";
import moment from "moment";

const isTimeInVisibility = (
  targetDate: string | undefined,
  targetTime: string | undefined,
  visibility:
    | Array<{
        active: boolean;
        day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
        deb: string;
        end: string;
      }>
    | undefined
): boolean => {
  if (!visibility || visibility.length === 0) {
    return true;
  }

  const referenceDate = targetDate ? moment(targetDate) : moment();
  const referenceTime = targetTime ? moment(targetTime, "HH:mm") : moment();
  const dayOfWeek = referenceDate.day();

  return visibility.some((period) => {
    if (!period.active) {
      return false;
    }

    if (period.day !== dayOfWeek) {
      return false;
    }

    const debTime = moment(period.deb, "HH:mm");
    const endTime = moment(period.end, "HH:mm");

    return (
      referenceTime.isSameOrAfter(debTime) &&
      referenceTime.isSameOrBefore(endTime)
    );
  });
};

export const useDiscountNudgeVAE = (): IDiscountNudgeVAE | undefined => {
  const { data: discounts = [] } = useGetDiscountNudgeVAEQuery();
  const [{ id_store, order_time, order_date }] = useCartModelChange([
    "id_store",
    "order_time",
    "order_date",
  ]);
  const [{ groups }] = useCustomerModelChange(["groups"]);

  return useMemo(() => {
    if (!discounts || discounts.length === 0) {
      return undefined;
    }

    const currentDate = new Date();
    // Comparaison en chaînes : groupes API/enum numériques vs customer_groups en chaînes
    const customerGroups = (groups || [ECustomerGroup.NOT_CONNECTED]).map(
      String
    );

    return discounts.find((discount) => {
      const availableFrom = new Date(discount.available_from);
      const availableTo = new Date(discount.available_to);
      const { customer_groups, stores, stores_exclusion, visibility } =
        discount;

      if (!(currentDate >= availableFrom && currentDate <= availableTo)) {
        return false;
      }

      if (customer_groups && customer_groups.length > 0) {
        const hasMatchingGroup = customer_groups.some((group) =>
          customerGroups.includes(String(group))
        );
        if (!hasMatchingGroup) {
          return false;
        }
      }

      if (stores && stores.length > 0) {
        if (!id_store) {
          return true;
        }

        const storeExclusion = stores_exclusion || EStoreExclusion.NONE;

        if (storeExclusion === EStoreExclusion.ONLY_SELECTED) {
          if (!stores.includes(id_store)) {
            return false;
          }
        } else if (storeExclusion === EStoreExclusion.EXCLUDE_SELECTED) {
          if (stores.includes(id_store)) {
            return false;
          }
        }
      }

      if (!isTimeInVisibility(order_date, order_time, visibility)) {
        return false;
      }

      return true;
    });
  }, [discounts, groups, id_store, order_date, order_time]);
};

export default useDiscountNudgeVAE;
